import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Game } from '@gains-com/shared';
import { environment } from '../../environments/environment';
import { map, take } from 'rxjs/operators';

export interface GameProviderVM {
  game_provider_id: string;
  name: string;
  alea_software_id: string | null;
  enabled: boolean;
  sort_rank: number | null;
  icon_url: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class GameService {
  private readonly _http = inject(HttpClient);

  getGameProviders$(isMerch: boolean) {
    return this._http
      .get<GameProviderVM[]>(`${environment.apiUrl}/game-providers`, {
        params: { is_merch: isMerch },
      })
      .pipe(
        map(providers => providers.map(p => ({
          id: p.game_provider_id,
          name: p.name,
        }))),
        take(1)
      );
  }

  getGames$({
    page,
    pageSize,
    enabled,
    gameTypeId,
    nameSearch,
    tags,
    providerId,
    featured,
    isMerch,
  }: {
    page: number;
    pageSize: number;
    enabled?: boolean;
    gameTypeId?: string;
    nameSearch?: string;
    tags?: string[];
    providerId?: string;
    featured?: boolean;
    isMerch?: boolean;
  }) {
    let httpParams = new HttpParams()
      .set('page', page)
      .set('pageSize', pageSize);

    if (enabled !== undefined) {
      httpParams = httpParams.set('enabled', enabled);
    }
    if (gameTypeId) {
      httpParams = httpParams.set('gameTypeId', gameTypeId);
    }
    if (providerId) {
      httpParams = httpParams.set('providerId', providerId);
    }
    if (nameSearch) {
      httpParams = httpParams.set('nameSearch', nameSearch);
    }
    if (featured) {
      httpParams = httpParams.set('featured', 'true');
    }
    if (tags?.length) {
      httpParams = httpParams.set('tags', JSON.stringify(tags));
    }
    if (isMerch !== undefined) {
      httpParams = httpParams.set('is_merch', isMerch);
    }

    return this._http
      .get<GameVM[]>(`${environment.apiUrl}/games`, { params: httpParams })
      .pipe(take(1));
  }

  getRecentlyAddedGames$(limit: number, isMerch: boolean) {
    const httpParams = new HttpParams().set('limit', limit).set('is_merch', isMerch);

    return this._http
      .get<GameVM[]>(`${environment.apiUrl}/games/recently-added`, { params: httpParams })
      .pipe(take(1));
  }
}

export interface GameVM extends Game {
  game_provider_name: string;
}
